import { useMemo, useRef } from 'react';
import { useGqlMutation, useGqlQuery } from '@API/useAvQuery';
import { useAvContext } from '@context/AvContextProvider';
import { FeatureFlags } from '@fabric-ui/components';
import { QueryKey } from '@fabric-ui/utils';
import useQueryObject from '@hooks/useQueryObjectSql';
import { isNullOrUndefined } from '@utils/utils';
import { gql } from 'graphql-request';
import { GetRiskScoringConfigResponse } from './types';
import { getFindingTypeDisplayName } from './utils';

const getMinFormula = (fieldName: string) => `MIN(${fieldName})`;

const FIELD_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*$/;
const isValidFieldName = (fieldName: string) => FIELD_NAME_PATTERN.test(fieldName);

export const useGetRiskScoringConfig = ({ projectionName }: { projectionName: string }) =>
  useGqlQuery<GetRiskScoringConfigResponse>({
    queryKey: [QueryKey.GetRiskScoringConfig, projectionName],
    query: GET_RISK_SCORING_CONFIG,
    variables: { projectionName },
  });

const GARTNER_MOCK_DATA: GetRiskScoringConfigResponse = {
  projectionToRiskScores: [
    {
      projectionName: 'uber_assets',
      riskScore: {
        id: null,
        accountId: '',
        updatedAt: null,
        updatedByUserId: null,
        projectionName: 'uber_assets',
        config: {
          baseScoreWeightContribution: [
            { projectionName: 'uber_findings', weight: 0.3, aggType: null },
            { projectionName: 'uber_assets', weight: 0.3, aggType: null },
            { projectionName: 'uber_policy_violations', weight: 0.2, aggType: null },
            { projectionName: 'uber_alerts', weight: 0.2, aggType: null },
          ],
        },
      },
    },
  ],
  projectionToBaseScores: [],
  riskScoreProjectionToBaseScoresMap: [],
};

export const useGetAllRiskScoringConfig = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const isGartnerDemo = !!useAvContext().featureFlags[FeatureFlags.GartnerDemo];
  const realResult = useGqlQuery<GetRiskScoringConfigResponse>({
    queryKey: [QueryKey.GetAllRiskScoringConfig],
    query: GET_ALL_RISK_SCORING_CONFIG,
    enabled,
  });
  if (isGartnerDemo && realResult.data) {
    const mockRiskScore = GARTNER_MOCK_DATA.projectionToRiskScores[0].riskScore;
    return {
      ...realResult,
      data: {
        ...realResult.data,
        projectionToRiskScores: realResult.data.projectionToRiskScores.map(entry => ({
          ...entry,
          riskScore: { ...entry.riskScore, config: mockRiskScore.config },
        })),
      },
    };
  }
  return realResult;
};

export const useCreateOrUpdateRiskScoringConfig = (onSuccess?: () => Promise<any>) =>
  useGqlMutation({
    mutationKey: [QueryKey.CreateOrUpdateRiskScoringConfig],
    mutation: CREATE_OR_UPDATE_RISK_SCORE,
    onSuccess,
  });

export const GET_RISK_SCORING_PREVIEW = gql`
  query riskScoringPreview($input: RiskScoringCreationRequestInput!, $selectivityFilter: filterScalar) {
    riskScoringPreview(input: $input, selectivityFilter: $selectivityFilter) {
      rows
      scriptError
      internalError
    }
  }
`;

export const useGetRiskScoringPreview = ({
  input,
  selectivityFilter,
  enabled = true,
  staleTime,
}: {
  input: any;
  selectivityFilter?: any;
  enabled?: boolean;
  staleTime?: number;
}) =>
  useGqlQuery({
    queryKey: [QueryKey.GetRiskScoringPreview, input, selectivityFilter],
    query: GET_RISK_SCORING_PREVIEW,
    variables: { input, selectivityFilter },
    select: (data: any) => data?.rows || [],
    enabled,
    staleTime,
  });

const GET_ALL_RISK_SCORING_CONFIG = gql`
  query getAllRiskScoringConfig {
    getAllRiskScoringConfig {
      projectionToRiskScores {
        projectionName
        riskScore {
          id
          accountId
          projectionName
          updatedByUserId
          updatedAt
          config {
            baseScoreWeightContribution {
              projectionName
              weight
              aggType
            }
          }
        }
      }
      projectionToBaseScores {
        projectionName
        baseScore {
          id
          accountId
          projectionName
          config {
            configTypes {
              type
              isDefault
              factorGroups {
                name
                weight
                factors {
                  displayName
                  fieldName
                  fallback
                  scoringRules {
                    value
                    filter
                    adjustmentType
                  }
                }
              }
            }
          }
        }
      }
      riskScoreProjectionToBaseScoresMap {
        riskScoreProjectionName
        baseScores {
          projectionName
          allowedAggTypes
        }
      }
    }
  }
`;

const CREATE_OR_UPDATE_RISK_SCORE = gql`
  mutation createOrUpdateRiskScoringConfig($input: RiskScoringCreationRequestInput!) {
    createOrUpdateRiskScoringConfig(input: $input)
  }
`;

export const useFieldsHasData = ({
  projectionName,
  fieldNames,
  skip,
}: {
  projectionName: string;
  fieldNames: (string | undefined)[];
  skip?: boolean;
}) => {
  const {
    accountEntities: { aggProjs },
  } = useAvContext();
  const isKnownProjection = !skip && !!aggProjs[projectionName]?.pathAlias;

  const cacheRef = useRef<Map<string, Map<string, boolean>>>(new Map());
  if (!cacheRef.current.has(projectionName)) {
    cacheRef.current.set(projectionName, new Map());
  }
  const projectionCache = cacheRef.current.get(projectionName)!;

  const uniqueFieldNames = useMemo(
    () => Array.from(new Set(fieldNames.filter((fieldName): fieldName is string => !!fieldName && isValidFieldName(fieldName)))),
    [fieldNames]
  );

  const uncachedFieldNames = useMemo(
    () => uniqueFieldNames.filter(fieldName => !projectionCache.has(fieldName)),
    [uniqueFieldNames, projectionCache]
  );

  const metrics = useMemo(
    () => (isKnownProjection ? uncachedFieldNames.map((fieldName, index) => ({ name: getMinFormula(fieldName), alias: `f${index}` })) : []),
    [isKnownProjection, uncachedFieldNames]
  );

  const { data, isLoading } = useQueryObject({
    queryObject: {
      select: { dims: [], metrics },
      projectionId: { name: projectionName, builtIn: false },
      sorting: [],
      top: { size: 1, groupOthers: false },
    },
    options: { enabled: metrics.length > 0 },
  });

  const populatedByField = useMemo(() => {
    if (skip) {
      return Object.fromEntries(uniqueFieldNames.map(fieldName => [fieldName, undefined])) as Record<string, boolean | undefined>;
    }

    const row = data?.[0];
    if (row) {
      uncachedFieldNames.forEach((fieldName, index) => projectionCache.set(fieldName, !isNullOrUndefined(row[`f${index}`])));
    }

    return Object.fromEntries(
      uniqueFieldNames.map(fieldName => [
        fieldName,
        projectionCache.has(fieldName) ? projectionCache.get(fieldName) : isLoading ? undefined : false,
      ])
    ) as Record<string, boolean | undefined>;
  }, [skip, data, isLoading, uniqueFieldNames, uncachedFieldNames, projectionCache]);

  return { populatedByField, isLoading: isLoading && uncachedFieldNames.length > 0 };
};

export const useFindingTypeOptions = (existingTypes: Set<string>) => {
  const { data: findingTypesData, isLoading: isLoadingFindingTypes } = useQueryObject({
    queryObject: {
      select: {
        dims: [
          {
            name: 'finding.type',
          },
        ],
        metrics: [],
      },
      distinct: true,
      projectionId: {
        name: 'uber_findings',
        builtIn: true,
      },
      sorting: [],
    },
  });

  const findingTypeOptions = useMemo(
    () =>
      (findingTypesData || [])
        .map(f => f['finding.type'])
        .filter(type => !isNullOrUndefined(type) && !existingTypes.has(type))
        .map(type => ({
          title: getFindingTypeDisplayName(type),
          value: type,
        })),
    [findingTypesData, existingTypes]
  );

  return { findingTypeOptions, isLoadingFindingTypes };
};

const GET_RISK_SCORING_CONFIG = gql`
  query getRiskScoringConfig($projectionName: String!) {
    getRiskScoringConfig(projectionName: $projectionName) {
      projectionToRiskScores {
        projectionName
        riskScore {
          id
          accountId
          projectionName
          updatedByUserId
          updatedAt
          config {
            baseScoreWeightContribution {
              projectionName
              weight
              aggType
            }
          }
        }
      }
      projectionToBaseScores {
        projectionName
        baseScore {
          id
          accountId
          projectionName
          config {
            configTypes {
              type
              factorGroups {
                name
                weight
                factors {
                  displayName
                  fieldName
                  fallback
                  scoringRules {
                    value
                    filter
                    adjustmentType
                  }
                }
              }
            }
          }
        }
      }
      riskScoreProjectionToBaseScoresMap {
        riskScoreProjectionName
        baseScores {
          projectionName
          allowedAggTypes
        }
      }
    }
  }
`;
